<script setup lang="ts">
import moment from 'moment';
import CalendarUtils from '~/client/utils/CalendarUtils';
import RawdataChart from './rawdata-chart.vue';
import DatePicker from './date-picker.vue';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
import { geoRawDataType, pvRawDataTypeSingle, pvRawDataTypeThree, solarRawDataType } from '~/enums';
import DateSet from './date-set.vue';

const props = defineProps<{
  apiData: any,
}>();

const equipStore = useEquipStore();
const tocStore = useTocStore();

const emits = defineEmits(['callAPI'])
const rawDataDate = ref(moment().format('YYYY-MM-DD'));

const specInfo = await equipStore.getEquipSpecInfo(equipStore.selectEquipInfo.cid);
const engSource = computed(() => tocStore.selectEquipInfo.engSource);

const reverseData = computed(() => {
  if(!props.apiData) return []

  return props.apiData.sort((a, b) => {
    if(a.receivedDate > b.receivedDate){
      return 1;
    }else if(a.receivedDate < b.receivedDate){
      return -1;
    } else {
      return 0;
    }

  })
})

const rawDataType = computed(() => {
  if(engSource.value === '10'){
    if(specInfo.pviPhase === 'single') return pvRawDataTypeSingle;
    else if(specInfo.pviPhase === 'three') return pvRawDataTypeThree;
  } else if(engSource.value === '20'){
    return solarRawDataType;
  } else if(engSource.value === '30'){
    return geoRawDataType;
  } else {
    return pvRawDataTypeSingle;
  }
})



const selectRawKey = ref(rawDataType.value[Object.keys(rawDataType.value)[0]]);
const selectRawLabel = computed(() => Object.keys(rawDataType.value)[Object.values(rawDataType.value).findIndex(v => v === selectRawKey.value)]);

const chartData = computed(() => reverseData.value ? reverseData.value.map(v => v[selectRawKey.value]) : []);
const chartLabels = computed(() => reverseData.value ? reverseData.value.map(v => moment(v.receivedDate).format('HH:mm')) : [])


function setDate(value: any) {
  // 동일한 날짜인 경우 API 호출 방지
  if (rawDataDate.value === value) {
    console.log('📅 동일한 날짜입니다. API 호출을 건너뜁니다.');
    return;
  }
  
  rawDataDate.value = value;
  emits('callAPI', {searchDate: rawDataDate.value});
  console.log('📅 날짜 변경됨:', rawDataDate.value);
}
</script>

<template>
<div class="rawdata-card category-chart common-card h-100">
  <div class="common-card__title">
    <h2 v-if="engSource !== '10'">
      계측정보
    </h2>
    <h2 v-else>
      계측정보
      {{ 
        specInfo.pviPhase === 'single' 
        ? '(단상)' 
        : specInfo.pviPhase === 'three' 
        ? '(삼상)' 
        : '' 
      }}
    </h2>
    <div class="select-area">
      <div class="select-wrapper">
        <div class="input-wrapper">
          <select class="select" v-model="selectRawKey">
            <option v-for="label in Object.keys(rawDataType)" :value="rawDataType[label]">{{ label }}</option>
          </select>
        </div>
      </div>
      
      <div class="date-select">
        <DatePicker 
          :modelValue="rawDataDate" 
          @setVmodel="setDate" 
          :monthPicker="false"
          :yearPicker="false"
          :teleportCenter="false"
          :pickerPosition="'left'"
        >
          <template #contents>
            <div class="input-wrapper">
              <input 
                class="calendar-icon text"
                type="text" 
                :value="moment(rawDataDate).format('YYYY-MM-DD')" 
              />
      
            </div>
          </template>
        </DatePicker>
      </div>
    </div>
  </div>
  <div class="content h-100">
    <RawdataChart :categories="chartLabels" :data="chartData" :chartKey="selectRawLabel" ></RawdataChart>
  </div>
</div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-toc/define_font.scss";
@use "@/assets/styles/design-toc/common-card.scss";

 .category-chart{
    border-radius: 10px;
    background: #F6F6F6;

    & .header{
      display: flex;
      justify-content: space-between;

      & .title {
        color: #262626;
        @include define_font.text-bold-16;
      }
    }
    
    & .select-area {
      display: flex;

      & .date-select {
        margin-left: 10px;
      }

      & .select-wrapper,
      & .date-select {
        & .input-wrapper {
          display: flex;

          & select {
            @include define_font.text-normal-14;
            width: 200px;
            height: 100%;
            padding: 10px 0px 10px 10px;
            background-size: 18px;

            color: var(--Grey-60, #363B3F);
            text-align: left;
          }

          
          & .calendar-icon {
            @include define_font.text-normal-14;
            width: 150px;
            text-indent: 25px;
            background: url("@/assets/images/toc/CalendarDots.svg") no-repeat scroll 5px;
            background-size: 18px;
            border: none;
            padding-top: 0;
            padding-bottom: 0;

            color: var(--Grey-60, #363B3F);
            text-align: left;
          }
          & .select {

            & option {
              padding: 0
            }
          }
          & .carot {
            border-left: none;       
            align-self: center;
            height: 100%;
            border-top-right-radius: 6px;
            border-bottom-right-radius: 6px;
            padding: 6px 4px;

            & img {
              width: 16px;
              height: 16px;
            }
          }
        }
      }
      & .date-select {
        align-items: center;
        border: 1px solid var(--input-border-color);
        border-radius: 6px;
        box-sizing: border-box;
        display: flex;
        height: 44px;
        justify-content: center;
      }
    }
  }
</style>